
import { FileText, FileSearch, FileEdit, FileCheck, GitMerge } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const DocumentAnalysisContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Document Analysis & Drafting</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered document analysis and drafting applications streamline creation, review, and management of legal documents.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered "Document Analysis and Drafting" applications for legal teams offer advanced features that streamline the creation, review, and management of legal documents. The benefits include faster document turnaround times, reduced errors, improved compliance, and the ability to focus on high-value tasks like strategy and client interaction. These tools enhance efficiency and maintain high standards of precision and professionalism when used in accordance with AI best practices.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <FileText size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Drafting</h3>
            <p className="text-white/70">
              AI generates initial drafts of contracts, agreements, and other legal documents using predefined templates and guidelines, ensuring consistency and saving significant time.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <FileSearch size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Document Summarization</h3>
            <p className="text-white/70">
              AI extracts key points from lengthy documents, highlighting critical clauses, risks, or obligations for faster review.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <FileCheck size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Risk Identification</h3>
            <p className="text-white/70">
              These tools flag inconsistencies, non-compliant terms, or potential legal risks within documents to enhance accuracy and compliance.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <FileEdit size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Language Optimization</h3>
            <p className="text-white/70">
              AI improves readability by suggesting clearer legal language or simplifying complex clauses.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <GitMerge size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Customizable Templates</h3>
            <p className="text-white/70">
              Legal teams can tailor templates for specific use cases, ensuring documents align with organizational standards and regulations.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Contract Review</h3>
            <p className="text-white/70 mb-4">
              How L'Oréal Uses AI to Review & Analyze Contracts Faster
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://ironcladapp.com/journal/webinars/how-loreal-performs-ai-contract-management/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Review Acceleration</h3>
            <p className="text-white/70 mb-4">
              Signifyd uses Ironclad to review contracts 85 times faster
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://ironcladapp.com/customers/signifyd/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Workflow Acceleration</h3>
            <p className="text-white/70 mb-4">
              How Bridgewater Accelerates Legal Workflows Using Harvey
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.harvey.ai/customers/bridgewater', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Critical Data Gathering</h3>
            <p className="text-white/70 mb-4">
              Gathering critical data for a high-profile bankruptcy case in under 24 hours
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.sirion.ai/library/case-study/eigen/gathering-critical-data-for-a-high-profile-bankruptcy-case-in-under-24-hours/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Contract Visibility</h3>
            <p className="text-white/70 mb-4">
              AI CLM Increases BNY Mellon's Visibility Into Over 78,000 Contracts
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.sirion.ai/library/case-study/bny-mellon/', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Optimize Your Document Workflows</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered document analysis and drafting solutions can transform your legal team's productivity.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/education">
            <Button variant="outline" size="lg">Explore Training Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysisContent;
